import { ComponentObservable, componentObserver } from '../../component-observer';
import { currentBreakpoint } from '../../utils/resize.utils';
import { FirstLevelMenuItem, getMenuItems, MenuType, SecondLevelMenuItem } from '../../api/menu-api';
import { Header, header, headerLevel } from '../header/header.module';
import { ECatalogProducts } from './e-catalog-products.module';
import { ECatalogOverview } from './e-catalog-overview.module';
import { ECatalogDetail } from './e-catalog-detail.module';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import { Icon } from '../../general/icons/Icon';
import { createHtmlElementFromString } from '../../utils/html.utils';
import { ECatalogCustom } from './e-catalog-custom.module';
import { CenterModule, CenterType } from '../../utils/center.module';

export enum ECatalogView {
  OVERVIEW,
  PRODUCTS,
  DETAIL,
  CUSTOM
}

enum ECatalogTabId {
  TAB_ALL = 'tab-all',
  TAB_OVERVIEW = 'tab-overview'
}

/**
 * Initializes and renders the e-catalog.
 */
class ECatalog implements ComponentObservable, CenterModule {
  /**
   * Selector of e-catalog that must be initialized in javascript.
   */
  componentSelector = '.e-catalog';

  /**
   * The translations for the e-catalog
   */
  translations: { [key: string]: string } = null;

  /**
   * Store the header menu items.
   */
  menuItems: FirstLevelMenuItem[] = [];

  /**
   * Store the last menu selection and search phrase.
   * This is the selection before the user searched using the search field. After
   * the user removes the search, this selection will be restored.
   */
  lastMenuSelection: {
    category: FirstLevelMenuItem;
    tab: SecondLevelMenuItem;
    searchPhrase: string;
  } = {
    category: null,
    tab: null,
    searchPhrase: null
  };

  /**
   * The last number of search results for the menu items.
   */
  lastTaxonomyCounts: Record<string, number>;
  lastTagCounts: Record<string, number>;

  /**
   * Currently selected first level menu item.
   */
  currentSelectedCategory: string = null;

  /**
   * If the header menu has been initialized
   */
  headerMenuInitialized = false;

  /**
   * The header component of the e-catalog.
   */
  eCatalogHeader: Header = null;

  /**
   * The view of the products with filter etc.
   */
  eCatalogProducts: ECatalogProducts = null;

  /**
   * The overview page for desktop only.
   */
  eCatalogOverview: ECatalogOverview = null;

  /**
   * The detail page when a product is selected.
   */
  eCatalogDetail: ECatalogDetail = null;

  /**
   * The custom page when a ventilator is selected
   */
  eCatalogCustom: ECatalogCustom = null;

  /**
   * The current view that is shown in the e-catalog.
   */
  currentView: ECatalogView = ECatalogView.OVERVIEW;

  /**
   * Abort controller used to cancel all event listeners on the tab elements (first level navigation).
   */
  tabListenersAbortController = new AbortController();

  /**
   * Markup for the shopping cart.
   */
  createShoppingCartMarkup(root: HTMLElement): string {
    return `<aside id="shopping-cart" class="shopping-cart" aria-hidden="true" data-error-message="${
      this.translations.errorMessage
    }">
    <div class="shopping-cart__header">
      <h4 class="shopping-cart__title">${this.translations.shoppingCart}</h4>
      <span class="shopping-cart__close-button">${Icon.closeSmall}</span>
    </div>
    <div class="shopping-cart__content">
      <div class="shopping-cart__items-wrapper"></div>
      <div class="shopping-cart__total-container">
        <span class="shopping-cart__total-label text-color-dark">${this.translations.totalSum} <span>${
      this.translations.exclVat
    }</span></span>
        <span class="shopping-cart__total text-color-dark"></span>
      </div>
    </div>
    <div class="shopping-cart__footer">
      <a href="${root.dataset.checkoutLink || ''}" class="button button--primary button--small-icon-spacing">
        <span class="button__text">${this.translations.checkout}</span>
        <span class="button__icon-right">${Icon.rightArrow}</span>
      </a>
    </div>
    <div class="shopping-cart__empty-state">
      <p class="shopping-cart__empty-state-title text-color-light">${this.translations.shoppingCartEmpty}</p>
      <p>${this.translations.shoppingCartEmptyHint}</p>
    </div>
  </aside>`;
  }

  /**
   * Markup for the wishlist
   */
  createWishlistMarkup(): string {
    return `<div>
  <aside id="wish-list" class="wish-list " aria-expanded="false" aria-hidden="true">
      <button class="wish-list__expand-button" aria-label="Expand wishlist">${Icon.expanded}</button>
      <div class="wish-list__header">
        <h4 class="wish-list__title">${this.translations.wishlistTitle}</h4>
        <span class="wish-list__close-button">${Icon.closeSmall}</span>
      </div>
      <div class="wish-list__content">
        <div class="wish-list__wrapper">
          <div class="wish-list__empty-state">
            <p class="wish-list__empty-state-title text-color-light">${this.translations.wishlistEmptyState}</p>
            <p>${this.translations.wishlistEmptyStateHint}</p>
          </div>
        </div>
        <div class="wish-list__reference-cards"></div>
      </div>
      <div class="wish-list__footer">
        <button class="button" data-overlay-id="new-wish-list-overlay" data-focus-input="true">
          <span class="button__text">${this.translations.wishlistAddNew}</span>
          <span class="button__icon-right">${Icon.add}</span>
        </button>
      </div>
    </aside>
    <div id="rename-wish-list-overlay" class="overlay-wrapper">
      <div class="overlay overlay--e-catalog overlay--wish-list-name">
        <button class="button button--overlay button--icon-only">
        <span class="button__icon-single">${Icon.close}</span>
        </button>
        <h4 class="e-catalog-overlay__title">${this.translations.wishlistRename}</h4>
        <div class="button-section button-section--right button-section--wish-list-input button-section--small-margin">
          <div class="form-control form-control--stretch">
            <label class="label" for="rename-wish-list-input">${this.translations.wishlistName}</label>
            <input class="input" id="rename-wish-list-input" aria-invalid="false" value="Inselspital Bern"/>
            <div class="helper-text"></div>
          </div>
          <button class="button button--primary">
            <span class="button__text">${this.translations.save}</span>
          </button>
        </div>
      </div>
    </div>
    <div id="new-wish-list-overlay" class="overlay-wrapper">
      <div class="overlay overlay--e-catalog overlay--wish-list-name">
        <button class="button button--overlay button--icon-only">
          <span class="button__icon-single">${Icon.close}</span>
        </button>
        <h4 class="e-catalog-overlay__title">${this.translations.wishlistAddNew}</h4>
        <div class="button-section button-section--wish-list-input button-section--right button-section--small-margin">
          <div class="form-control form-control--stretch">
            <label class="label" for="new-wish-list-name-input">${this.translations.wishlistName}</label>
            <input class="input" id="new-wish-list-name-input" aria-invalid="false"/>
            <div class="helper-text"></div>
          </div>
          <button class="button button--primary select-wish-list-overlay-create-list-button" disabled>
            <span class="button__text">${this.translations.wishlistCreate}</span>
            <span class="button__icon-right">${Icon.add}</span>
          </button>
        </div>
      </div>
    </div>
    <div id="delete-wish-list-overlay" class="overlay-wrapper">
    <div class="overlay overlay--e-catalog">
    <button class="button button--overlay button--icon-only">
    <span class="button__icon-single">${Icon.close}</span>
    </button>
    <h4 class="e-catalog-overlay__title">${this.translations.wishlistConfirmDelete}</h4>
    <div class="button-section button-section--right button-section--row-spacing button-section--small-margin">
      <button class="button" id="cancel-delete-wish-list-button">
        <span class="button__text">${this.translations.cancel}</span>
      </button>
      <button class="button button--primary" id="confirm-delete-wish-list-button">
        <span class="button__text">${this.translations.delete}</span>
      </button>
    </div>
  </div></div>
    <div id="select-wish-list-overlay" class="overlay-wrapper" 
      data-label-wishlist-added="${this.translations.wishlistAdded}" 
      data-label-wishlist-removed="${this.translations.wishlistRemoved}"
      data-label-wishlist-go-to="${this.translations.wishlistGoTo}"
      data-error-message="${this.translations.errorMessage}">
      <div class="overlay overlay--e-catalog overlay--wish-list-name">
        <button class="button button--overlay button--icon-only">
            <span class="button__icon-single">${Icon.close}</span>
        </button>
        <h4 class="e-catalog-overlay__title">${this.translations.wishlistAdd}</h4>
        <div class="list-selection"></div>
        <div class="button-section button-section--wish-list-input button-section--right button-section--vertical-margin-32">
          <div class="form-control form-control--stretch">
            <label class="label" for="select-wish-list-input">${this.translations.wishlistName}</label>
            <input class="input" id="select-wish-list-input" aria-invalid="false" />
            <div class="helper-text"></div>
          </div>
          <button class="button button--primary select-wish-list-overlay-create-list-button" disabled>
            <span class="button__text">${this.translations.wishlistCreate}</span>
            <span class="button__icon-right">${Icon.add}</span>
          </button>
        </div>
        <div class="button-section button-section--right">
          <button class="button button--primary select-wish-list-overlay-save-button">
            <span class="button__text">${this.translations.save}</span>
          </button>
        </div>
      </div>
    </div>
  </div>`;
  }

  /**
   * Create the e-catalog. Fetches the menu items and initializes the sub components
   */
  createECatalog(root: HTMLElement): void {
    // add class to html element, for specific e-catalog styling
    document.documentElement.classList.add('html--e-catalog');

    // Create header template
    this.createHeader(root);

    // Add shopping cart and wishlist to the e-catalog
    this.createShoppingCartAndWishList(root);

    // and header menu, if not already rendered
    if (!this.headerMenuInitialized) {
      // otherwise render the header menu
      this.fetchHeaderMenuItems().then(async (menuItems) => {
        this.headerMenuInitialized = true;
        this.menuItems = menuItems;
        this.renderHeaderMenu(menuItems);
        this.createSubComponents(root);
      });
    }
  }

  /**
   * Create the shopping cart and wish list.
   */
  createShoppingCartAndWishList(root: HTMLElement): void {
    root.append(createHtmlElementFromString(this.createShoppingCartMarkup(root)));
    this.setupWishlist(root);
  }

  /**
   * Creates the template for the header and setup translations.
   * @param root The e-catalog root element
   */
  createHeader(root: HTMLElement): void {
    // Create header from template and append it to the e-catalog
    const headerTemplate = createHtmlElementFromString(
      header.createTemplate(this.translations.headerTitle, this.translations.more, CenterType.E_CATALOG)
    );
    root.append(headerTemplate);

    // Set translations
    headerTemplate.dataset.labelAllDevices = this.translations.allVentilators;
    headerTemplate.dataset.labelAll = this.translations.all;
    headerTemplate.dataset.labelOverview = this.translations.overview;
    headerTemplate.dataset.labelMobileFilter = this.translations.filter;
    headerTemplate.dataset.labelMobileResetFilter = this.translations.mobileResetFilter;
    headerTemplate.dataset.labelMobileBackToAllCategoriesButton = this.translations.mobileBackToAllCategories;

    // If there was a ventilator ID in the URL, preselect it in the ventilator dropdown
    const ventilatorIdFromUrl = readUrlParameter(Param.VENTILATOR);
    if (ventilatorIdFromUrl) {
      headerTemplate.dataset.selectedDevice = ventilatorIdFromUrl;
    }
  }

  /**
   * Render the header menu with first and second level items.
   */
  renderHeaderMenu(menuItems: FirstLevelMenuItem[]): void {
    const headerElement = document.querySelector<HTMLElement>('.section--header');
    headerElement.dataset.tabs = JSON.stringify(menuItems);

    const subMenus = {};
    for (const item of menuItems) {
      subMenus[item.id] = item.secondaryCategories;
    }

    headerElement.dataset.subMenu = JSON.stringify(subMenus);
    header.initialize(headerElement).then(() => {
      this.setupProductSearch(headerElement);
    });
  }

  /**
   * Initialize listeners for the product search input field and the cancel search button.
   */
  setupProductSearch(headerElement: HTMLElement): void {
    const headerSearchField = headerElement.querySelector<HTMLInputElement>('.search-field__input');

    // if the URL contains a previous search phrase -> set it
    const searchPhraseFromUrl = readUrlParameter(Param.SEARCH_PHRASE);
    if (searchPhraseFromUrl) {
      headerSearchField.value = decodeURIComponent(searchPhraseFromUrl);

      // show the search field when a search phrase was given
      header.toggleSearch(headerElement, false);
      // show the all tab
      this.showAllCategoriesTab(headerElement, true);
    }

    // listen for changes in the input field (load results when the user submits the search)
    headerSearchField.addEventListener(
      'change',
      () => {
        setTimeout(() => {
          const currentInput = headerSearchField.value;
          const currentCategory = this.getFirstLevelMenuItemFromId(readUrlParameter(Param.CATEGORY));
          const currentTab = this.getSecondLevelMenuItemFromId(readUrlParameter(Param.TAB));

          if (currentInput.length > 0) {
            // add the search phrase to the url
            writeToUrl({ param: Param.SEARCH_PHRASE, value: encodeURIComponent(currentInput) }, [
              Param.PRODUCT,
              Param.CUSTOM_URL
            ]);

            this.eCatalogProducts.updateFilterWithNewSearchPhrase(
                currentInput,
                this.currentView === ECatalogView.PRODUCTS
            );
            this.updateMobileMenuHeadingAndBackButton(headerElement, null, null, 'results');
          } else {
            this.removeSearchText();
            return;
          }

          if (this.currentView !== ECatalogView.PRODUCTS) {
            // remove any product id from the URL
            writeToUrl([], [Param.PRODUCT, Param.CUSTOM_URL], false, false);
            // if we are not on the products page, switch to it
            this.currentView = ECatalogView.PRODUCTS;
            this.renderView();
          }

          // show the 'all' tab only if there has not been a search phrase before, otherwise just keep the current menu selection
          if (this.lastMenuSelection.searchPhrase === null) {
            const breakpoint = currentBreakpoint();
            const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;
            if (!isMobile) {
              // on mobile don't select the 'all' tab automatically. Stay on the first level menu
              this.changeHeaderMenuForSearch(headerElement);
            } else {
              this.eCatalogProducts.updateFirstLevelMenuSelection(null, true);
              this.showAllCategoriesTab(headerElement, true);
            }

            // save the current menu selection, so that we can return to it after completing the search
            this.lastMenuSelection.category = currentCategory;
            this.lastMenuSelection.tab = currentTab;
          }

          // save the current search phrase so next time the user searches we know if it was the first search or follow up search
          this.lastMenuSelection.searchPhrase = currentInput;
        }, 1200);
      },
      false
    );
    // reset the search when the user clicks the 'remove text' button in the search field
    const removeTextButton = headerElement.querySelector<HTMLElement>('#search-field-remove-text-button');
    removeTextButton.addEventListener(
      'click',
      () => {
        this.removeSearchText();
      },
      false
    );
  }

  /**
   * Reset the filters (sidebar). This function gets called by the header when the user selects
   * the 'all' tab of the tab bar.
   */
  resetFilters(): void {
    this.eCatalogProducts?.resetFilters();
  }

  /**
   * Add a filter for a ventilator with the given ID. This function is called from the header,
   * when the user selects a ventilator from the dropdown.
   */
  updateFilterWithNewVentilator(itemId: string): void {
    this.eCatalogProducts?.updateFilterWithNewVentilator(itemId);
  }

  /**
   * Remove the search phrase from the search input field. This clears the input field, removes the search filter,
   * and restores the menu and sub menu selection to the state before searching.
   */
  removeSearchText(): void {
    const parameters = [];
    const parametersToRemove = [Param.SEARCH_PHRASE];

    if (this.lastMenuSelection.category?.id) {
      parameters.push({ param: Param.CATEGORY, value: this.lastMenuSelection.category.id });
    } else {
      parametersToRemove.push(Param.CATEGORY);
      parametersToRemove.push(Param.TAB);
    }

    if (this.lastMenuSelection.tab?.id) {
      parameters.push({ param: Param.TAB, value: this.lastMenuSelection.tab.id });
    } else {
      parametersToRemove.push(Param.TAB);
    }

    // also remove it from the URL
    writeToUrl(parameters, parametersToRemove, false, true);
    window.location.reload();
  }

  /**
   * Update the search result tags for the first and second level menu items.
   * If they are not visible yet, they will get visible.
   */
  updateSearchResultCountTags(taxonomyCounts: Record<string, number>): void {
    if (taxonomyCounts) {
      const eCatalogElement = document.querySelector('.e-catalog');
      const menuTabs = eCatalogElement.querySelectorAll<HTMLElement>(
        'ul[role="tablist"] li, .sub-menu .sub-menu__item'
      );

      // update the menu items counter tags
      for (const menuTab of menuTabs) {
        const tabId = menuTab.firstElementChild.id;
        if (tabId !== ECatalogTabId.TAB_ALL && tabId !== ECatalogTabId.TAB_OVERVIEW && tabId !== 'sub-menu_all') {
          // only set a number if it is not the all or overview tab
          const resultCount = taxonomyCounts[tabId];
          const hasResults = resultCount > 0;

          if (hasResults) {
            const counterTag = menuTab.querySelector<HTMLElement>('.tag');

            // show the tag
            menuTab.classList.remove('selector-item--hidden');
            counterTag.classList.remove('tag--hidden');

            // update the number of results for the tab
            counterTag.innerText = resultCount.toString();
          } else {
            // hide the whole menu item
            menuTab.classList.add('selector-item--hidden');
          }
        }
      }

      // update the mobile header count tag
      let taxonomyForResults;
      const currentTab = readUrlParameter(Param.TAB);
      const currentCategory = readUrlParameter(Param.CATEGORY);
      if (currentTab && currentTab !== 'all') {
        taxonomyForResults = currentTab;
      } else if (currentCategory && currentCategory !== 'all') {
        taxonomyForResults = currentCategory;
      }
      if (taxonomyForResults) {
        const mobileHeaderCounterTag = eCatalogElement.querySelector<HTMLElement>(
          '.navigation-mobile__selected-category .tag'
        );
        if (mobileHeaderCounterTag) {
          mobileHeaderCounterTag.innerText = `${taxonomyCounts[taxonomyForResults]}`;
        }
      }
    }
  }

  /**
   * Hides the result tags. This is necessary when the user removes the search.
   */
  hideSearchResultCountTagsAndShowAllMenuItems(): void {
    const headerElement = document.querySelector('.section--header');
    const categoryTabs = headerElement.querySelectorAll<HTMLElement>(
      'ul[role="tablist"] li, .sub-menu .sub-menu__item'
    );
    for (const categoryTab of categoryTabs) {
      const counterTag = categoryTab.querySelector<HTMLElement>('.tag');
      counterTag?.classList.add('tag--hidden');

      // show menu items again, that were previously hidden because they contained no results
      const tabId = categoryTab.firstElementChild.id;
      if (tabId !== ECatalogTabId.TAB_ALL && tabId !== ECatalogTabId.TAB_OVERVIEW) {
        categoryTab.classList.remove('selector-item--hidden');
      }
    }
  }

  /**
   * Programmatically select a category and tab in the header menu.
   * @param headerElement: The header root element
   * @param categoryId: The id of the category that should get selected or null for the overview page.
   * @param tabId: The id of the tab that should get selected or null for no selection.
   * @param preventReload: Do not reload the product page, only update the header selection.
   */
  changeHeaderMenuSelection(
    headerElement: HTMLElement,
    categoryId: string,
    tabId: string,
    preventReload = false
  ): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;
    const actualTabId = tabId !== 'all' ? tabId : null;
    const customUrl = readUrlParameter(Param.CUSTOM_URL);

    if (!categoryId && !customUrl && !isMobile) {
      // if no category and customUrl given on desktop -> switch to the overview page
      categoryId = ECatalogTabId.TAB_OVERVIEW;
    } else if (!categoryId && !customUrl && isMobile) {
      // show the first level menu again
      header.showMobileMenu(headerElement);
    }

    // deselect the currently active tab
    const selectedTab = tabList.querySelector('.selector-item--active');
    selectedTab?.classList.remove('selector-item--active');

    if (categoryId) {
      // set the category (first level item) that should get selected as active
      // querySelector with attribute selector is necessary because numbers as IDs are not valid CSS selectors
      tabList.querySelector(`[id='${categoryId}']`).parentElement.classList.add('selector-item--active');

      // create the sub menu for the respective first level option
      const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');
      headerSubNavigation.removeAttribute('aria-hidden');
      this.createSubMenu(headerElement, categoryId, actualTabId);
    }

    if (categoryId === ECatalogTabId.TAB_ALL) {
      // if the 'all' category should get selected -> display it
      this.showAllCategoriesTab(headerElement, true);
    } else if (categoryId === ECatalogTabId.TAB_OVERVIEW) {
      // if the 'overview' category should get selected -> display it
      this.showAllCategoriesTab(headerElement, false);
    }

    // update the URL parameters to reflect the current category/tab selection
    if (categoryId === ECatalogTabId.TAB_ALL) {
      writeToUrl({ param: Param.CATEGORY, value: 'all' });
      this.eCatalogProducts.updateFirstLevelMenuSelection(null, false);

      if (isMobile) {
        // On mobile hide the menu if the 'all' category was selected -> directly go to the results
        header.hideMobileMenu(headerElement);
      }
    } else if (categoryId === ECatalogTabId.TAB_OVERVIEW || !categoryId) {
      writeToUrl([], [Param.CATEGORY], true, true);
      this.eCatalogProducts.updateFirstLevelMenuSelection(null, false);
    } else {
      writeToUrl({ param: Param.CATEGORY, value: categoryId });
      this.eCatalogProducts.updateFirstLevelMenuSelection(this.getFirstLevelMenuItemFromId(categoryId), !preventReload);
    }

    // if not on the overview page, reload the product catalog
    const regularCategoryWasSelected = categoryId !== ECatalogTabId.TAB_OVERVIEW;
    const reload = !preventReload && regularCategoryWasSelected;
    if (tabId !== null) {
      writeToUrl({ param: Param.TAB, value: tabId }, null, false, false);
      if (tabId !== 'all') {
        this.eCatalogProducts.updateSecondLevelMenuSelection(this.getSecondLevelMenuItemFromId(tabId), reload);
      }
    } else {
      writeToUrl([], [Param.CATEGORY], true, true);
      this.eCatalogProducts.updateSecondLevelMenuSelection(null, reload);
    }

    if (!regularCategoryWasSelected && !isMobile) {
      // If the overview category was selected on desktop -> go to overview page
      this.currentView = ECatalogView.OVERVIEW;
      this.renderView();
    }
  }

  /**
   * When coming from the overview page (clicked on a category card), this function changes the header menu selection
   * to the selected category.
   * @param headerElement: The header root element
   * @param categoryId: The id of the category that should get selected or null for the overview page.
   */
  changeHeaderFromOverview(headerElement: HTMLElement, categoryId: string): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');

    // deselect the currently active tab
    const selectedTab = tabList.querySelector('.selector-item--active');
    selectedTab?.classList.remove('selector-item--active');

    if (categoryId) {
      // set the category (first level item) that should get selected as active
      // querySelector with attribute selector is necessary because numbers as IDs are not valid CSS selectors
      tabList.querySelector(`[id='${categoryId}']`).parentElement.classList.add('selector-item--active');

      // create the sub menu for the respective first level option
      const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');
      headerSubNavigation.removeAttribute('aria-hidden');
      this.createSubMenu(headerElement, categoryId, null);
    }

    // update the URL parameters to reflect the current category/tab selection
    writeToUrl({ param: Param.CATEGORY, value: categoryId });
    this.eCatalogProducts.updateFirstLevelMenuSelection(this.getFirstLevelMenuItemFromId(categoryId));
  }

  /**
   * When coming from a page that is not the product list (e.g. overview, detail, etc), this function changes
   * the header menu selection to all/all.
   * @param headerElement: The header root element
   */
  changeHeaderMenuForSearch(headerElement: HTMLElement): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;

    // deselect the currently active tab
    const selectedTab = tabList.querySelector('.selector-item--active');
    selectedTab?.classList.remove('selector-item--active');

    // create the sub menu for the all tab
    const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');
    headerSubNavigation.removeAttribute('aria-hidden');
    this.createSubMenu(headerElement, ECatalogTabId.TAB_ALL, null);

    // set the all tab as active
    // querySelector with attribute selector is necessary because numbers as IDs are not valid CSS selectors
    tabList.querySelector('[id="tab-all"]').parentElement.classList.add('selector-item--active');

    // if the 'all' category should get selected -> display it
    this.showAllCategoriesTab(headerElement, true);

    // update the URL parameters to reflect the current category/tab selection
    writeToUrl(
      [
        { param: Param.CATEGORY, value: 'all' },
        { param: Param.TAB, value: 'all' }
      ],
      null,
      true,
      true
    );
    this.eCatalogProducts.updateFirstLevelMenuSelection(null, true);

    if (isMobile) {
      // On mobile hide the menu if the 'all' category was selected -> directly go to the results
      header.hideMobileMenu(headerElement);
    }
  }

  /**
   * Reset the text input that the user typed into the search field.
   */
  resetProductSearchInputField(): void {
    const headerElement = document.querySelector<HTMLElement>('.section--header');
    const headerSearchField = headerElement.querySelector<HTMLInputElement>('.search-field__input');
    headerSearchField.value = '';
  }

  /**
   * Renders the content depending on the current view.
   */
  renderView(): void {
    const eCatalogRoot = document.querySelector<HTMLElement>(this.componentSelector);
    switch (this.currentView) {
      case ECatalogView.OVERVIEW:
        this.eCatalogProducts.hide();
        this.eCatalogDetail.reset();
        this.eCatalogCustom.reset();
        this.eCatalogOverview.render(eCatalogRoot, this.menuItems, true);
        break;
      case ECatalogView.PRODUCTS:
        this.eCatalogOverview.reset();
        this.eCatalogDetail.reset();
        this.eCatalogCustom.reset();
        this.eCatalogProducts.render(eCatalogRoot);
        this.eCatalogProducts.show();
        break;
      case ECatalogView.DETAIL:
        this.eCatalogOverview.reset();
        this.eCatalogProducts.hide();
        this.eCatalogDetail.reset();
        this.eCatalogCustom.reset();
        this.eCatalogDetail.render(eCatalogRoot, null, true, true);
        break;
      case ECatalogView.CUSTOM:
        this.eCatalogOverview.reset();
        this.eCatalogProducts.hide();
        this.eCatalogDetail.reset();
        this.eCatalogCustom.reset();
        this.eCatalogCustom.render(eCatalogRoot);
    }
  }

  /**
   * Shows/hides the 'all' categories tab in the first level menu. If the 'all' tab is shown, the 'overview' tab is immediately hidden.
   * @param header The header element
   * @param show If the all tab should be visible
   */
  showAllCategoriesTab(header: HTMLElement, show: boolean): void {
    const allTab = header.querySelector<HTMLElement>(`#${ECatalogTabId.TAB_ALL}`)?.parentElement;
    const overviewTab = header.querySelector<HTMLElement>(`#${ECatalogTabId.TAB_OVERVIEW}`)?.parentElement;
    if (allTab) {
      allTab.classList.toggle('selector-item--hidden', !show);
      overviewTab.classList.toggle('selector-item--hidden-desktop', show);
    }
  }

  /**
   * Creates the tab navigation
   * @param headerElement The header element
   * @param selectedCategoryId
   */
  createTabNavigation(headerElement: HTMLElement, selectedCategoryId: string): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');

    const labelOverview = headerElement.dataset.labelOverview;
    if (headerElement.dataset.tabs) {
      const tabs = JSON.parse(headerElement.dataset.tabs);
      if (!tabs || !labelOverview) {
        // eslint-disable-next-line no-console
        console.error('Tabs and/or label for overview is missing');
      } else {
        // create the 'overview' item
        let items = `<li role="presentation" class="selector-item selector-item--hidden-mobile${
          selectedCategoryId === ECatalogTabId.TAB_OVERVIEW ? ' selector-item--active' : ''
        }"><a role="tab" id="${ECatalogTabId.TAB_OVERVIEW}" tabindex="0">${labelOverview}</a></li>`;

        items += `<li role="presentation" class="selector-item selector-item--hidden${
          selectedCategoryId === ECatalogTabId.TAB_ALL ? ' selector-item--active' : ''
        }" aria-hidden="true"><a role="tab" id="${ECatalogTabId.TAB_ALL}">${this.translations.all}</a></li>`;

        tabs.forEach((tab) => {
          items += `<li role="presentation" class="selector-item${
            selectedCategoryId === tab.id ? ' selector-item--active' : ''
          }"><a role="tab" id="${tab.id}" tabindex="0">${
            tab.label || tab.name
          }<span class="tag tag--counter tag--hidden"></span></a></li>`;
        });
        tabList.innerHTML = items;

        if (selectedCategoryId === ECatalogTabId.TAB_ALL) {
          // hide the sub menu if necessary (second level)
          this.showAllCategoriesTab(headerElement, true);
        }
      }
      // Add click listener to tabs to render the sub menu
      let tabElements = tabList.querySelectorAll<HTMLElement>('[role="tab"]');
      const toggleActiveState = (tab: HTMLElement): void => {
        const selectedTab = tabList.querySelector('.selector-item--active');
        selectedTab?.classList.remove('selector-item--active');
        tab.parentElement.classList.add('selector-item--active');
        const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');

        // Remove product and custom page from url on tab click
        const paramsToRemove = [Param.PRODUCT, Param.CUSTOM_URL, Param.CATEGORY, Param.TAB];

        // Set view to render
        if (tab.id !== ECatalogTabId.TAB_OVERVIEW) {
          if (tab.id !== ECatalogTabId.TAB_ALL) {
            // if the all tab was selected, the sub menu will not be displayed
            headerSubNavigation.removeAttribute('aria-hidden');
            this.createSubMenu(headerElement, tab.id, null);
          }

          const menuItem = this.getFirstLevelMenuItemFromId(tab.id);
          // only reload the products if we are on the product page already. Otherwise the reload is triggered by the URL change below
          this.eCatalogProducts.updateFirstLevelMenuSelection(menuItem, this.currentView === ECatalogView.PRODUCTS);
          const categoryIdForUrl = tab.id === ECatalogTabId.TAB_ALL ? 'all' : tab.id;
          writeToUrl(
            [
              { param: Param.CATEGORY, value: categoryIdForUrl },
              { param: Param.TAB, value: 'all' }
            ],
            [Param.PRODUCT, Param.CUSTOM_URL]
          );
        } else {
          // go to overview (no GET param necessary)
          writeToUrl(null, paramsToRemove);
        }
        this.eCatalogHeader.updateMobileTabsOnSelect(headerElement);

        // store the current tab selection
        this.currentSelectedCategory = tab.id;
      };

      // Event listener to select a tab when a dropdown item is selected
      tabList.parentElement.addEventListener('ontabselected', (event: CustomEvent) =>
        toggleActiveState(event.detail.firstElementChild)
      );

      // Make sure that all visible tabs have click and key listeners
      tabList.parentElement.addEventListener('ontabschanged', () => {
        tabElements = tabList.querySelectorAll<HTMLElement>('[role="tab"]');

        // Remove first to avoid multiple listeners
        this.tabListenersAbortController.abort();
        this.tabListenersAbortController = new AbortController();

        tabElements.forEach((tab) => {
          // Set listeners
          tab.addEventListener('click', () => toggleActiveState(tab), {
            signal: this.tabListenersAbortController.signal
          });
          tab.addEventListener('keypress', (event) => (event.key === 'Enter' ? toggleActiveState(tab) : false), {
            signal: this.tabListenersAbortController.signal
          });
        });
      });
    }
  }

  /**
   * Creates the sub menu depending on the current tab
   * @param header
   * @param currentCategoryId The id of the category that is active
   * @param currentTabId The id of the tab that is active or null/'all' if 'all' tab should be active
   */
  createSubMenu(header: HTMLElement, currentCategoryId: string, currentTabId: string): void {
    const subMenuContainer = header.querySelector<HTMLElement>('.sub-menu');
    const breakpoint = currentBreakpoint();

    const headerSearchField = header.querySelector<HTMLInputElement>('.search-field__input');
    const currentSearchPhrase = headerSearchField.value;

    if (currentCategoryId === ECatalogTabId.TAB_OVERVIEW && (breakpoint.isTablet || breakpoint.isDesktop)) {
      subMenuContainer.parentElement.setAttribute('aria-hidden', 'true');
    } else if (header.dataset.subMenu && currentCategoryId !== ECatalogTabId.TAB_OVERVIEW) {
      const subMenu = JSON.parse(header.dataset.subMenu);

      // collect the sub menu items that need to be displayed
      let subMenuItems = [];

      if (currentCategoryId === ECatalogTabId.TAB_ALL) {
        subMenuContainer.classList.add('sub-menu--hidden-desktop');

        // if switching to the all tab, display all possible tabs from all categories
        for (const subMenuItemsList in subMenu) {
          subMenuItems = subMenuItems.concat(subMenu[subMenuItemsList]);
        }
      } else {
        subMenuContainer.classList.remove('sub-menu--hidden-desktop');

        // if a specific category was selected, display the tabs for that category
        subMenuItems = subMenu[currentCategoryId];
      }

      let items = `<li class="sub-menu__item${
        currentTabId === 'all' || currentTabId === null ? ' sub-menu__item--active' : ''
      }"><a id="sub-menu_all" tabindex="0">${this.translations.all}</a></li>`;
      if (subMenuItems) {
        subMenuItems.forEach((item) => {
          items += `<li class="sub-menu__item${currentTabId === item.id ? ' sub-menu__item--active' : ''}"><a id="${
            item.id
          }" tabindex="0">${item.label || item.name}<span class="tag tag--counter tag--hidden"></span></a></li>`;
        });
        subMenuContainer.innerHTML = items;
      } else {
        subMenuContainer.parentElement.setAttribute('aria-hidden', 'true');
      }

      if (currentSearchPhrase) {
        // If the user is currently searching, update the number of results for the sub menu items.
        this.updateSearchResultCountTags(this.lastTaxonomyCounts);
      }

      const toggleActiveState = (item: HTMLElement): void => {
        const selectedItem = subMenuContainer.querySelector('.sub-menu__item--active');
        selectedItem?.classList.remove('sub-menu__item--active');
        item.parentElement.classList.add('sub-menu__item--active');
        this.eCatalogHeader.updateMobileSecondLevelNavigation(header, subMenuContainer);
        // Remove product from url on tab click
        writeToUrl([], [Param.PRODUCT], true, true);
        if (item.id !== 'sub-menu_all') {
          writeToUrl({ param: Param.TAB, value: item.id });
          // update the filter for this second level menu item
          const menuItem = this.getSecondLevelMenuItemFromId(item.id);
          this.eCatalogProducts.updateSecondLevelMenuSelection(menuItem);
        } else {
          writeToUrl({ param: Param.TAB, value: 'all' });
          this.eCatalogProducts.updateSecondLevelMenuSelection(null);
        }

        // update the mobile headings
        const firstLevelItem = this.getFirstLevelMenuItemFromId(currentCategoryId);
        const secondLevelItem = this.getSecondLevelMenuItemFromId(item.id);
        this.updateMobileMenuHeadingAndBackButton(header, firstLevelItem, secondLevelItem, 'results');
      };

      // Add click listener to sub menu entries
      const menuItems = subMenuContainer.querySelectorAll<HTMLElement>('.sub-menu__item a');
      menuItems.forEach((item) => {
        item.addEventListener('click', () => toggleActiveState(item));
        item.addEventListener('keypress', (event) => (event.key === 'Enter' ? toggleActiveState(item) : false));
      });

      if (currentCategoryId && currentTabId && (breakpoint.isMobileLandscape || breakpoint.isMobilePortrait)) {
        const firstLevelItem = this.getFirstLevelMenuItemFromId(currentCategoryId);
        const secondLevelItem = this.getSecondLevelMenuItemFromId(currentTabId);

        // When a category+tab are selected we need to display the search results immediately without menu/sub menu selection
        this.eCatalogHeader.hideMobileMenu(header);
        // update the mobile headings
        setTimeout(() => {
          this.updateMobileMenuHeadingAndBackButton(header, firstLevelItem, secondLevelItem, 'results');
        }, 200);
      } else {
        // When only a category is selected from the URL and the user opens the page on mobile
        const firstLevelItem = this.getFirstLevelMenuItemFromId(currentCategoryId);

        setTimeout(() => {
          this.updateMobileMenuHeadingAndBackButton(header, firstLevelItem, null, 'sub-menu');
        }, 200);
      }
    }
  }

  /**
   * Update the menu heading and back button on mobile. Also shows/hides the filter on mobile.
   * @param header: Header root element.
   * @param category: The currently selected category.
   * @param tab: The currently selected tab or null if the 'all' tab has been selected.
   * @param level: If the mobile menu is currently showing the first or second level of the menu or the product results.
   */
  updateMobileMenuHeadingAndBackButton(
    header: HTMLElement,
    category: FirstLevelMenuItem,
    tab: SecondLevelMenuItem,
    level: headerLevel
  ): void {
    const currentTabLabel = header.querySelector<HTMLElement>('.navigation-mobile__selected-category');
    const mobileBackButton = header.querySelector<HTMLElement>('.navigation-mobile__back');
    const categoryContainer = header.querySelector<HTMLElement>('.navigation-mobile__category-container');

    if (currentTabLabel) {
      let menuHeading, backButtonLabel;
      const showCounter = level === 'results' && readUrlParameter(Param.SEARCH_PHRASE) !== null && category;

      if (level === 'sub-menu') {
        // when on the sub menu, display the name of the selected category (first level) as a heading
        // and as a label for the back button display something like 'back to categories'.
        menuHeading = category?.name;
        backButtonLabel = this.translations.mobileBackToAllCategories;
      } else if (level === 'results') {
        // when on the results page, either display the name of the selected tab (second level) as a heading
        // or if the 'all' option for a tab was selected, display the name of the category
        // The back button should be labeled with the category if there is a specific tab selected, otherwise
        // (if an 'all' tab is selected) it should be labeled with something like 'back to categories'.
        menuHeading = tab?.name || category?.name || this.translations.all;
        backButtonLabel = tab
          ? category.name
          : category
          ? this.translations.mobileBackToSubCategories
          : this.translations.mobileBackToAllCategories;
      } else if (level === 'detail') {
        menuHeading = '';
        backButtonLabel = this.translations.backToProducts;
      }

      // insert the heading before the counter tag
      const tabLabelContainsText = currentTabLabel.firstChild.nodeName.toLowerCase() !== 'span';
      if (tabLabelContainsText) {
        currentTabLabel.firstChild.nodeValue = menuHeading;
      } else {
        currentTabLabel.insertBefore(document.createTextNode(menuHeading), currentTabLabel.firstElementChild);
      }

      // hide or show the counter tag
      const counterTag = currentTabLabel.querySelector('.tag');
      counterTag.classList.toggle('tag--hidden', !showCounter);

      // add the label to the back button
      mobileBackButton.innerText = backButtonLabel;

      // and show the filter
      categoryContainer.classList.toggle('navigation-mobile__category-container--filter-visible', level === 'results');
    }
  }

  /**
   * Retrieve the header menu items.
   */
  fetchHeaderMenuItems(): Promise<FirstLevelMenuItem[]> {
    return getMenuItems(MenuType.MENU_E_CATALOG);
  }

  /**
   * Get the first level menu item object from its ID. The menu items
   * are stored in this objects this.menuItems property.
   */
  getFirstLevelMenuItemFromId(menuItemId: string): FirstLevelMenuItem {
    let menuItem = null;
    for (const item of this.menuItems) {
      if (item.id === menuItemId) {
        menuItem = item;
      }
    }
    return menuItem;
  }

  /**
   * Get the second level menu item object from its ID. The menu items
   * are stored in this objects this.menuItems property.
   */
  getSecondLevelMenuItemFromId(menuItemId: string): SecondLevelMenuItem {
    let menuItem = null;
    for (const item of this.menuItems) {
      for (const secondLevelItem of item.secondaryCategories) {
        if (secondLevelItem.id === menuItemId) {
          menuItem = secondLevelItem;
        }
      }
    }
    return menuItem;
  }

  /**
   * Initialize all e-catalogs on the current page.
   * @param observe Boolean if the ECatalogModule should listen for changes in the DOM and initialize dynamically added e-catalogs.
   */
  initializeAll(observe: boolean): void {
    const allECatalogs = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allECatalogs.length; i++) {
      this.initialize(allECatalogs[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize e catalogs when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Creates the objects for the components that are used in the e-catalog.
   * @param root The e-catalog element
   */
  createSubComponents(root: HTMLElement): void {
    const categoryIdFromUrl = readUrlParameter(Param.CATEGORY);
    const tabIdFromUrl = readUrlParameter(Param.TAB);
    const searchPhraseFromUrl = readUrlParameter(Param.SEARCH_PHRASE);
    const customUrl = readUrlParameter(Param.CUSTOM_URL);

    // Create header if not exists
    if (this.eCatalogHeader === null) {
      const headerElement = root.querySelector<HTMLElement>('.section--header');
      this.eCatalogHeader = header.prepareAndReturnHeader(false, this);

      let selectedCategoryId = categoryIdFromUrl || (customUrl ? null : ECatalogTabId.TAB_OVERVIEW);

      if (selectedCategoryId === 'all') {
        // the url only has 'all' for the all tab -> but we need the id of the all tab which is 'tab-all'
        selectedCategoryId = ECatalogTabId.TAB_ALL;
      }
      this.createTabNavigation(headerElement, selectedCategoryId);

      // when there was a category in the URL, use that one, otherwise show the overview page
      const selectedTabIdFromUrl = readUrlParameter(Param.TAB) || null;
      this.createSubMenu(headerElement, selectedCategoryId, selectedTabIdFromUrl);
    }

    // Create e-catalog overview (desktop only)
    if (this.eCatalogOverview === null) {
      this.eCatalogOverview = new ECatalogOverview(this.translations);
    }

    // Create view of the products with filters
    if (this.eCatalogProducts === null) {
      const pathsParameter = root.dataset.paths || '';
      this.eCatalogProducts = new ECatalogProducts(this.translations, pathsParameter);
      // Get the menu item object, so we can look up which filters we need
      const firstLevelItemId = categoryIdFromUrl || null;
      const firstLevelMenuItem = this.getFirstLevelMenuItemFromId(firstLevelItemId);
      this.eCatalogProducts.updateFirstLevelMenuSelection(firstLevelMenuItem, false);

      if (firstLevelMenuItem) {
        // If there was a tab in the URL, select that one, otherwise select the 'all' tab.
        const secondLevelItemId = tabIdFromUrl || 'sub-menu_all';
        const secondLevelMenuItem = this.getSecondLevelMenuItemFromId(secondLevelItemId);
        this.eCatalogProducts.updateSecondLevelMenuSelection(secondLevelMenuItem, false);
      }

      if (searchPhraseFromUrl) {
        this.eCatalogProducts.updateFilterWithNewSearchPhrase(decodeURIComponent(searchPhraseFromUrl), false);
      }
    }

    // Create detail view
    if (this.eCatalogDetail === null) {
      this.eCatalogDetail = new ECatalogDetail(this.translations);

      // listen for url changes that are triggered by navigation in the e-catalog
      window.addEventListener('urlchange', () => {
        this.renderPageDependingOnUrlData(true);
      });

      // listen for url changes that are triggered by browser back/forward buttons
      window.addEventListener('popstate', () => {
        window.location.reload();
      });
    }

    // Create custom view
    if (this.eCatalogCustom === null) {
      this.eCatalogCustom = new ECatalogCustom(this.translations);
    }

    // Check which page needs to be displayed
    this.renderPageDependingOnUrlData();

    // Render current view
    this.renderView();
  }

  /**
   * Checks if the url contains any product or page info and render the respective page.
   */
  renderPageDependingOnUrlData(reload?: boolean): void {
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobileLandscape || breakpoint.isMobilePortrait;

    const product = readUrlParameter(Param.PRODUCT);
    const searchPhrase = readUrlParameter(Param.SEARCH_PHRASE);
    const category = readUrlParameter(Param.CATEGORY);
    const custom = readUrlParameter(Param.CUSTOM_URL);
    if (product) {
      writeToUrl([], [Param.CUSTOM_URL], true, true);
      this.currentView = ECatalogView.DETAIL;
    } else if (category || searchPhrase) {
      writeToUrl([], [Param.CUSTOM_URL], true, true);
      this.currentView = ECatalogView.PRODUCTS;
    } else if (custom) {
      this.currentView = ECatalogView.CUSTOM;
    } else {
      writeToUrl([], [Param.CUSTOM_URL], true, true);
      this.currentView = isMobile ? ECatalogView.PRODUCTS : ECatalogView.OVERVIEW;
    }

    if (reload) {
      this.renderView();
    }
  }

  /**
   * Initialize the given e-catalog.
   * @param root The e-catalog root element that should be initialized
   */
  initialize(root: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, root);

    if (!this.translations) {
      this.translations = JSON.parse(root.dataset.translations);
    }

    // render
    this.createECatalog(root);
  }

  /**
   * Creates the wishlist with the translations
   * @param root The e-catalog element
   */
  setupWishlist(root: HTMLElement): void {
    const wishlistWrapper = createHtmlElementFromString(this.createWishlistMarkup());
    const wishlist = wishlistWrapper.firstElementChild as HTMLElement;
    // Setup dataset translations
    wishlist.dataset.addToCartButtonLabel = this.translations.addToShoppingCart;
    wishlist.dataset.downloadPdfButtonLabel = this.translations.downloadPdf;
    wishlist.dataset.productLengthLabel = this.translations.productLength;
    wishlist.dataset.productCompatibilityLabel = this.translations.productCompatibility;
    wishlist.dataset.productPriceLabel = this.translations.price;
    wishlist.dataset.productPatientGroupsLabel = this.translations.patientGroups;
    wishlist.dataset.productUsageLabel = this.translations.use;
    wishlist.dataset.productPackagingUnitLabel = this.translations.packingUnit;
    wishlist.dataset.emptyListHint = this.translations.emptyWishlistHint;
    wishlist.dataset.usageIconSingleUseTitle = this.translations.singleUse;
    wishlist.dataset.usageIconReusableTitle = this.translations.reusable;
    wishlist.dataset.usageIconAutoclavableTitle = this.translations.autoclavable;
    wishlist.dataset.patientGroupActiveLabel = this.translations.active;
    wishlist.dataset.patientGroupInactiveLabel = this.translations.inactive;
    wishlist.dataset.patientGroupNeonatalTitle = this.translations.neonatal;
    wishlist.dataset.patientGroupPediatricTitle = this.translations.pediatric;
    wishlist.dataset.patientGroupAdultTitle = this.translations.adults;
    wishlist.dataset.errorMessage = this.translations.errorMessage;
    wishlist.dataset.addedToShoppingCart = this.translations.addedToShoppingCart;
    wishlist.dataset.goToShoppingCart = this.translations.goToShoppingCart;

    root.append(wishlistWrapper);
  }

  /**
   * Called from the header when the print button was clicked.
   */
  printPage(): void {
    // regular browser print dialog for all pages except the product list (which downloads a pdf file)
    if (this.currentView !== ECatalogView.PRODUCTS) {
      window.print();
    } else if (this.eCatalogProducts.productResults?.listPdfLink) {
      // Open PDF file in new window
      window.open(this.eCatalogProducts.productResults.listPdfLink, '_blank').focus();
    } else {
      window.print();
    }
  }
}

const eCatalogModule = new ECatalog();
export { eCatalogModule as eCatalog };
